/**
 * The browser-specific parts of push: where a blocked permission is undone,
 * and whether this is an iPhone or iPad tab that could receive push once the
 * site is on the Home Screen.
 *
 * iOS delivers web push only to a site added to the Home Screen and opened
 * from there (iOS 16.4 and later). In a Safari tab the Notification API does
 * not exist, so without this check the opt-in silently never appears and the
 * person cannot find out why.
 */

/**
 * The name the installed app carries on a Home Screen and in the phone's
 * notification settings. It must match `short_name` in
 * public/manifest.webmanifest (Android's label) and the
 * `apple-mobile-web-app-title` meta tag in index.html (iOS's label);
 * push-platform.test.ts checks both.
 */
export const HOME_SCREEN_NAME = "HBK Connect";

function isIos(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  // iPadOS reports itself as a Mac; a touch screen gives it away.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

function isAndroid(): boolean {
  return /Android/.test(navigator.userAgent);
}

function isStandalone(): boolean {
  const legacy = (navigator as Navigator & { standalone?: boolean }).standalone;
  return (
    legacy === true ||
    (typeof window.matchMedia === "function" &&
      window.matchMedia("(display-mode: standalone)").matches)
  );
}

/** The iOS version, from `OS 17_5` on iPhone or `Version/17.5` on an iPad
 *  posing as a Mac. Null when neither is present. */
function iosVersion(): number | null {
  const ua = navigator.userAgent;
  const match = /OS (\d+)_(\d+)/.exec(ua) ?? /Version\/(\d+)\.(\d+)/.exec(ua);
  if (match === null) return null;
  return Number(match[1]) + Number(match[2]) / 100;
}

/**
 * A browser that can put this site on the Home Screen: Safari, or since iOS
 * 16.4 Chrome, Edge and Firefox. The browsers built into other apps
 * (LinkedIn, Gmail, Facebook, Instagram and so on) cannot, and most of them
 * leave the `Safari/` token out of their user agent.
 */
function canAddToHomeScreen(): boolean {
  const ua = navigator.userAgent;
  if (!/Safari\//.test(ua)) return false;
  return !/FBAN|FBAV|Instagram|LinkedInApp|GSA\/|Line\/|MicroMessenger|Twitter|Snapchat|Pinterest/.test(
    ua,
  );
}

/**
 * An iPhone or iPad browser tab where adding the site to the Home Screen
 * would give it push: iOS 16.4 or later, in a browser that offers Add to
 * Home Screen, and not already opened from there.
 */
export function isIosBrowserTab(): boolean {
  if (!isIos() || isStandalone()) return false;
  const version = iosVersion();
  if (version === null || version < 16.04) return false;
  return canAddToHomeScreen();
}

/** How to turn a blocked notification permission back on, in this browser. */
export function unblockInstructions(): string {
  if (isIos()) {
    return `Open the Settings app, go to Notifications, choose ${HOME_SCREEN_NAME} and turn on Allow Notifications.`;
  }
  if (isStandalone()) {
    // An installed app has no address bar to click.
    if (isAndroid()) {
      return `Press and hold the ${HOME_SCREEN_NAME} icon on your Home Screen, open App info, then Notifications, and turn them on.`;
    }
    return "Open the app menu at the top of this window, choose App info, then Site settings, and set Notifications to Allow.";
  }
  const ua = navigator.userAgent;
  const safari =
    /Safari\//.test(ua) && !/Chrome|Chromium|CriOS|Edg|Firefox|FxiOS/.test(ua);
  if (safari) {
    return "In Safari, open Settings, then Websites, then Notifications, and set this site to Allow.";
  }
  return "Open this site's settings from the icon next to the address bar, set Notifications to Allow, then come back to this page.";
}

/** What an iPhone or iPad tab says instead of offering push. */
export const INSTALL_FOR_PUSH = `To get notifications on iPhone or iPad, add this site to your Home Screen: tap Share, then Add to Home Screen, and open ${HOME_SCREEN_NAME} from there.`;
